import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import manufacturingPhase2Service, {
  Operation,
  BOMOperation,
  OperationDependency,
  OperationTimeLog,
  OperationType,
  OperationStatus,
} from '@/services/manufacturing-phase2.service';
import { getCompanyIdFromStorage } from '@/utils/jwtPermissions';

const getCompanyId = () => getCompanyIdFromStorage();

// ========================================
// Operations Master Hooks
// ========================================

export const useOperations = (params?: { search?: string; active_only?: boolean }) => {
  const company_id = getCompanyId();
  return useQuery({
    queryKey: ['operations', company_id, params],
    queryFn: async () => {
      const response = await manufacturingPhase2Service.operations.getAll(company_id || '', params);
      console.log('[useOperations] API Response:', response);
      // Handle different response structures
      if (Array.isArray(response.data)) {
        return response.data;
      }
      if (response.data?.data && Array.isArray(response.data.data)) {
        return response.data.data;
      }
      if (response.data?.success && Array.isArray(response.data.data)) {
        return response.data.data;
      }
      console.warn('[useOperations] Unexpected response structure:', response.data);
      return [];
    },
    enabled: true, // Always enabled, backend will handle company_id fallback
  });
};

export const useOperation = (id: string) => {
  return useQuery({
    queryKey: ['operation', id],
    queryFn: async () => {
      const response = await manufacturingPhase2Service.operations.getById(id);
      const raw = response.data as { data?: Operation } | Operation | undefined;
      if (raw && typeof raw === 'object' && 'data' in raw && raw.data) return raw.data;
      return raw as Operation;
    },
    enabled: !!id,
  });
};

export const useOperationsByType = (type: OperationType) => {
  const company_id = getCompanyId();
  return useQuery({
    queryKey: ['operations', 'type', type, company_id],
    queryFn: async () => {
      const response = await manufacturingPhase2Service.operations.getByType(company_id, type);
      return response.data.data || response.data;
    },
    enabled: !!company_id && !!type,
  });
};

export const useCreateOperation = () => {
  const queryClient = useQueryClient();
  const company_id = getCompanyId();

  return useMutation({
    mutationFn: (data: Partial<Operation>) =>
      manufacturingPhase2Service.operations.create({ ...data, company_id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['operations'] });
      message.success('Operation created successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to create operation');
    },
  });
};

export const useUpdateOperation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Operation> }) =>
      manufacturingPhase2Service.operations.update(id, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['operations'] });
      queryClient.invalidateQueries({ queryKey: ['operation', variables.id] });
      message.success('Operation updated successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to update operation');
    },
  });
};

export const useDeleteOperation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => manufacturingPhase2Service.operations.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['operations'] });
      message.success('Operation deleted successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to delete operation');
    },
  });
};

export const useActivateOperation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => manufacturingPhase2Service.operations.activate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['operations'] });
      message.success('Operation activated successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to activate operation');
    },
  });
};

export const useDeactivateOperation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => manufacturingPhase2Service.operations.deactivate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['operations'] });
      message.success('Operation deactivated successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to deactivate operation');
    },
  });
};

// ========================================
// BOM Operations (Routing) Hooks
// ========================================

export const useBOMOperations = (bomId: string) => {
  return useQuery({
    queryKey: ['bomOperations', bomId],
    queryFn: async () => {
      const response = await manufacturingPhase2Service.bomOperations.getByBOM(bomId);
      return response.data.data || response.data;
    },
    enabled: !!bomId,
  });
};

export const useBOMOperation = (id: string) => {
  return useQuery({
    queryKey: ['bomOperation', id],
    queryFn: async () => {
      const response = await manufacturingPhase2Service.bomOperations.getById(id);
      return response.data.data || response.data;
    },
    enabled: !!id,
  });
};

export const useCreateBOMOperation = () => {
  const queryClient = useQueryClient();
  const company_id = getCompanyId();

  return useMutation({
    mutationFn: (data: Partial<BOMOperation>) =>
      manufacturingPhase2Service.bomOperations.create({ ...data, company_id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bomOperations'] });
      message.success('Routing operation created successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to create routing operation');
    },
  });
};

export const useUpdateBOMOperation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<BOMOperation> }) =>
      manufacturingPhase2Service.bomOperations.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bomOperations'] });
      message.success('Routing operation updated successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to update routing operation');
    },
  });
};

export const useDeleteBOMOperation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => manufacturingPhase2Service.bomOperations.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bomOperations'] });
      message.success('Routing operation deleted successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to delete routing operation');
    },
  });
};

export const useResequenceBOMOperations = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ bomId, operations }: { bomId: string; operations: Array<{ id: string; sequence_number: number }> }) =>
      manufacturingPhase2Service.bomOperations.resequence(bomId, operations),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bomOperations'] });
      message.success('Operations resequenced successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to resequence operations');
    },
  });
};

export const useBOMTotalTime = (bomId: string, quantity: number) => {
  return useQuery({
    queryKey: ['bomTotalTime', bomId, quantity],
    queryFn: async () => {
      const response = await manufacturingPhase2Service.bomOperations.calculateTotalTime(bomId, quantity);
      return response.data.data || response.data;
    },
    enabled: !!bomId && quantity > 0,
  });
};

export const useBOMQualityCheckpoints = (bomId: string) => {
  return useQuery({
    queryKey: ['bomQualityCheckpoints', bomId],
    queryFn: async () => {
      const response = await manufacturingPhase2Service.bomOperations.getQualityCheckpoints(bomId);
      return response.data.data || response.data;
    },
    enabled: !!bomId,
  });
};

// ========================================
// Operation Dependencies Hooks
// ========================================

export const useOperationDependencies = (bomId: string) => {
  return useQuery({
    queryKey: ['operationDependencies', bomId],
    queryFn: async () => {
      const response = await manufacturingPhase2Service.operationDependencies.getByBOM(bomId);
      return response.data.data || response.data;
    },
    enabled: !!bomId,
  });
};

export const useCreateOperationDependency = () => {
  const queryClient = useQueryClient();
  const company_id = getCompanyId();

  return useMutation({
    mutationFn: (data: Partial<OperationDependency>) =>
      manufacturingPhase2Service.operationDependencies.create({ ...data, company_id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['operationDependencies'] });
      message.success('Dependency created successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to create dependency');
    },
  });
};

export const useDeleteOperationDependency = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => manufacturingPhase2Service.operationDependencies.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['operationDependencies'] });
      message.success('Dependency deleted successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to delete dependency');
    },
  });
};

// ========================================
// Operation Time Logs Hooks
// ========================================

export const useOperationTimeLogs = (workOrderId: string) => {
  return useQuery({
    queryKey: ['operationTimeLogs', workOrderId],
    queryFn: async () => {
      const response = await manufacturingPhase2Service.operationTimeLogs.getByWorkOrder(workOrderId);
      return response.data.data || response.data;
    },
    enabled: !!workOrderId,
  });
};

export const useOperationTimeLogsByStatus = (status: OperationStatus) => {
  const company_id = getCompanyId();
  return useQuery({
    queryKey: ['operationTimeLogs', 'status', status, company_id],
    queryFn: async () => {
      const response = await manufacturingPhase2Service.operationTimeLogs.getByStatus(company_id, status);
      return response.data.data || response.data;
    },
    enabled: !!company_id,
  });
};

export const useStartOperation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => manufacturingPhase2Service.operationTimeLogs.startOperation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['operationTimeLogs'] });
      message.success('Operation started successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to start operation');
    },
  });
};

export const useCompleteOperation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      manufacturingPhase2Service.operationTimeLogs.completeOperation(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['operationTimeLogs'] });
      message.success('Operation completed successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to complete operation');
    },
  });
};

export const usePauseOperation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => manufacturingPhase2Service.operationTimeLogs.pauseOperation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['operationTimeLogs'] });
      message.success('Operation paused successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to pause operation');
    },
  });
};

export const useResumeOperation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => manufacturingPhase2Service.operationTimeLogs.resumeOperation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['operationTimeLogs'] });
      message.success('Operation resumed successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to resume operation');
    },
  });
};

